import React, { useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import Select from "react-select";
import * as apiLearning from "../../../api/learning";
import { isLearningAdmin } from "./learningPermissions";
// import QuizResults from "./components/QuizResults";
import QuizBuilder from "./components/builder/QuizBuilder";
import { createQuizTemplate } from "../components/quiz/quizTemplate";
import * as yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faSave, faDownload, faExclamationTriangle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import zIndex from "@material-ui/core/styles/zIndex";

// ✅ Helper: toast semplice (puoi sostituire con notistack/toastify in futuro)
const showToast = (message, type = "info") => {
  const id = `toast-${Date.now()}`;
  const color = type === "error" ? "tw:bg-red-500" : type === "success" ? "tw:bg-green-500" : "tw:bg-blue-500";
  const el = document.createElement("div");
  el.id = id;
  el.innerHTML = `
    <div class="tw:fixed tw:top-24 tw:right-4 tw:max-w-md tw:z-50 tw:px-4 tw:py-3 tw:rounded-sm tw:text-white tw:shadow-lg ${color} tw:flex tw:items-center tw:gap-2">
      <span>${message}</span>
      <button class="tw:ml-auto tw:text-white/80 hover:tw:text-white" onclick="document.getElementById('${id}').remove()">✕</button>
    </div>
  `;
  document.body.appendChild(el);
  setTimeout(() => {
    const e = document.getElementById(id);
    if (e) e.remove();
  }, 4000);
};

export default function QuizAdmin() {
  const { user } = useSelector(({ auth }) => auth, shallowEqual);
  const token = user?.authToken;
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [videoId, setVideoId] = useState("");
  const [quizId, setQuizId] = useState("");
  const [quizTitle, setQuizTitle] = useState("");
  const [quizObj, setQuizObj] = useState(() => createQuizTemplate());
  const [errors, setErrors] = useState({});
  const [builderKey, setBuilderKey] = useState(0);
  const [lockedQuestionIds, setLockedQuestionIds] = useState([]);
  const [hasResults, setHasResults] = useState(false);
  const [loading, setLoading] = useState({ save: false, load: false, delete: false, videos: false });
  const [confirmModal, setConfirmModal] = useState({ open: false, onConfirm: null, message: "" });
  

  const myRole = parseInt(user.idRole);

  const isAllowed = isLearningAdmin(myRole);

  // ✅ Caricamento video
  useEffect(() => {
    if (!token || !isAllowed) return;
    setLoading((prev) => ({ ...prev, videos: true }));
    apiLearning
      .getVideos(token)
      .then((data) => setVideos(Array.isArray(data) ? data : []))
      .catch(() => showToast("Errore caricamento video", "error"))
      .finally(() => setLoading((prev) => ({ ...prev, videos: false })));
  }, [token, isAllowed]);

  

  const videoOptions = useMemo(() => videos.map((v) => ({ value: v.video_id, label: `${v.video_id} • ${v.title}` })), [
    videos,
  ]);

  

  const handleSelectVideo = (opt) => {
    if (!opt) {
      setSelected(null);
      setVideoId("");
      setQuizId("");
      setQuizTitle("");
      setQuizObj(createQuizTemplate());
      setErrors({});
      return;
    }
    const v = videos.find((x) => x.video_id === opt.value);
    setSelected(v || null);
    const newVideoId = v?.video_id || "";
    const newTitle = v?.title || "";
    const newQuizId = newVideoId ? `quiz-${newVideoId}` : "";
    setVideoId(newVideoId);
    setQuizTitle(newTitle ? `Quiz • ${newTitle}` : "Quiz");

    setQuizId(newQuizId);
    const initial = createQuizTemplate({
      id: newQuizId,
      videoId: newVideoId,
      title: newTitle ? `Quiz • ${newTitle}` : "Quiz",
    });
    setQuizObj(initial);
  };

  const saveQuiz = async () => {
    if (!token || !videoId || !quizId) {
      showToast("Completa i campi obbligatori", "error");
      return;
    }
    if (Object.keys(errors).length > 0) {
      showToast("Correggi gli errori prima di salvare", "error");
      return;
    }
    setLoading((prev) => ({ ...prev, save: true }));
    try {
      const payload = { ...quizObj, id: quizId, videoId, title: quizTitle };
      await apiLearning.setQuiz(token, payload);
      showToast("✅ Quiz salvato con successo", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || e?.message || "Errore salvataggio"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const loadQuiz = async (idOverride) => {
    const id = idOverride || videoId;
    if (!token || !id) {
      showToast("Seleziona prima un video", "error");
      return;
    }
    setLoading((prev) => ({ ...prev, load: true }));
    try {
      const res = await apiLearning.getQuizByVideo(token, id);
      const q = res?.quiz;
      if (q && q.json) {
        const parsed = JSON.parse(q.json);
        setQuizId(q.quiz_id || parsed.id || quizId);
        setQuizTitle(q.title || parsed.title || quizTitle);
        setQuizObj(parsed);
        try {
          const results = await apiLearning.getQuizResults(token, { quizId: q.quiz_id || parsed.id });
          const hasRes = Array.isArray(results) && results.length > 0;
          setLockedQuestionIds(hasRes ? (Array.isArray(parsed.questions) ? parsed.questions.map((x) => x.id).filter(Boolean) : []) : []);
          setHasResults(hasRes);
        } catch {}
        setBuilderKey((k) => k + 1);
        showToast("✅ Quiz caricato", "success");
      } else {
        showToast("ℹ️ Nessun quiz salvato per questo video", "info");
        setLockedQuestionIds([]);
        setHasResults(false);
      }
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || e?.message || "Errore caricamento"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, load: false }));
    }
  };

  const deleteQuiz = async () => {
    if (!token || !videoId) return;

    // ✅ Sostituisce confirm()
    setConfirmModal({
      open: true,
      message: "Eliminare il quiz? Questa azione è irreversibile.",
      onConfirm: async () => {
        setConfirmModal({ open: false, onConfirm: null, message: "" });
        // → logica di eliminazione qui dentro
        await performDelete();
      },
    });
  };

  // Estrai la logica in una funzione separata
  const performDelete = async () => {
    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      await apiLearning.deleteQuiz(token, { videoId, quizId });
      setQuizObj(createQuizTemplate({ id: quizId, videoId, title: quizTitle }));
      setBuilderKey((k) => k + 1);
      showToast("🗑️ Quiz eliminato", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || e?.message || "Errore eliminazione"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  // ✅ Validazione sync (non async per input semplici)
  useEffect(() => {
    const schema = yup.object({
      videoId: yup
        .string()
        .trim()
        .required("Video ID obbligatorio"),
      quizId: yup
        .string()
        .trim()
        .required("Quiz ID obbligatorio"),
      quizTitle: yup
        .string()
        .trim()
        .required("Titolo obbligatorio"),
    });
    try {
      schema.validateSync({ videoId, quizId, quizTitle }, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const next = {};
      if (err && err.inner) {
        err.inner.forEach((e) => {
          if (e.path && !next[e.path]) next[e.path] = e.message;
        });
      }
      setErrors(next);
    }
  }, [videoId, quizId, quizTitle]);

  // ✅ Stile per react-select con tw- prefix
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.2)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
      borderRadius: "0.5rem",
      padding: "2px",
    }),
    menu: (base) => ({ ...base, zIndex: 99999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : "transparent",
      color: state.isSelected ? "white" : "inherit",
    }),
  };

  const QuizActionBar = ({ onLoad, onSave, onDelete, loadingLoad, loadingSave, loadingDelete, disableSave }) => (
    <div className="tw:flex tw:gap-2">
      <button
        type="button"
        className="tw:inline-flex tw:items-center tw:gap-2 tw:px-4 tw:py-2.5 tw:outline-2 tw:outline-green-600 tw:text-green-600 tw:font-medium tw:rounded-sm tw:shadow tw:transition tw:duration-200"
        onClick={() => onLoad()}
        disabled={loadingLoad}
        aria-label="Carica quiz esistente"
      >
        {loadingLoad ? (
          <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faDownload} />
        )}
        Carica
      </button>

      <button
        type="button"
        className="tw:inline-flex tw:items-center tw:gap-2 tw:px-4 tw:py-2.5 tw:outline-2 tw:outline-red-400 tw:text-red-400 tw:font-medium tw:rounded-sm tw:shadow tw:transition tw:duration-200"
        onClick={() => onSave()}
        disabled={loadingSave || disableSave}
        aria-label="Salva quiz"
      >
        {loadingSave ? (
          <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faSave} />
        )}
        Salva
      </button>

      <button
        type="button"
        className="tw:inline-flex tw:outline-2 tw:outline-rose-600 tw:items-center tw:gap-2 tw:px-4 tw:py-2.5 tw:bg-rose-600 hover:tw:bg-rose-700 tw:text-white tw:font-medium tw:rounded-sm tw:shadow tw:transition tw:duration-200"
        onClick={() => onDelete()}
        disabled={loadingDelete}
        aria-label="Elimina quiz"
      >
        {loadingDelete ? (
          <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faTrashAlt} />
        )}
        Elimina
      </button>
    </div>
  );

  return (
    <div className="tw:max-w-12xl tw:mx-auto tw:px-4 tw:py-6 tw:space-y-6">
      {!isAllowed ? (
        <div className="tw:bg-amber-50 tw:border tw:border-amber-200 tw:text-amber-800 tw:rounded-xl tw:p-4 tw:text-center">
          🔒 Questa area è riservata agli amministratori, agli sviluppatori e ai grafici.
        </div>
      ) : (
        <>
          {/* === Selezione video === */}
          <section className="tw:bg-white tw:rounded-xl tw:shadow-xs tw:overflow-hidden tw:border tw:border-gray-100">
            <div className="tw:bg-gradient-to-r tw:from-white-50 tw:to-white-50 tw:px-6 tw:py-4">
              <h2 className="tw:text-xl  tw:text-gray-800">
                Seleziona un video per creare o modificare il quiz
              </h2>
            </div>
            <div className="tw:p-6">
              <div className="tw:space-y-4">
                <div>
                  <label htmlFor="video-select" className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                    Video
                  </label>
                  <Select
                    id="video-select"
                    classNamePrefix="tw"
                    options={videoOptions}
                    value={
                      selected ? { value: selected.video_id, label: `${selected.video_id} • ${selected.title}` } : null
                    }
                    onChange={async (opt) => {
                      handleSelectVideo(opt);
                      await loadQuiz(opt ? opt.value : undefined);
                    }}
                    placeholder="— Seleziona un video —"
                    noOptionsMessage={() => "Nessun video disponibile"}
                    isLoading={loading.videos}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    aria-label="Seleziona video"
                  />
                </div>

                {selected && (
                  <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4 tw:mt-4 tw:p-4 tw:bg-blue-50 tw:rounded-sm">
                    <div>
                      <dt className="tw:text-xs tw:font-medium tw:text-blue-800">Video ID</dt>
                      <dd className="tw:mt-1 tw:text-sm tw:font-mono tw:text-blue-900">{videoId}</dd>
                    </div>
                    <div>
                      <dt className="tw:text-xs tw:font-medium tw:text-blue-800">Titolo</dt>
                      <dd className="tw:mt-1 tw:text-sm tw:text-blue-900">{selected.title}</dd>
                    </div>
                    <div>
                      <dt className="tw:text-xs tw:font-medium tw:text-blue-800">Quiz ID</dt>
                      <dd className="tw:mt-1 tw:text-sm tw:font-mono tw:text-blue-900">
                        {quizId || "— non impostato —"}
                      </dd>
                    </div>
                  </div>
                )}

                {Object.keys(errors).length > 0 && (
                  <div className="tw:mt-4 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-sm tw:text-sm tw:text-red-700 tw:flex tw:items-start tw:gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                    <div>
                      <strong>Attenzione:</strong> Completa tutti i campi obbligatori prima di procedere.
                    </div>
                  </div>
                )}
              </div>
        </div>
      </section>

      {/* === Costruttore + azioni === */}
          {selected && (
            <section className="tw:bg-white tw:rounded-xl tw:shadow-md tw:overflow-hidden tw:border tw:border-gray-100">
              <div className="tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4 tw:bg-gray-50">
                <h2 className="tw:text-xl tw:font-bold tw:text-gray-800">Creazione Quiz</h2>
                <QuizActionBar
                  onLoad={loadQuiz}
                  onSave={saveQuiz}
                  onDelete={deleteQuiz}
                  loadingLoad={loading.load}
                  loadingSave={loading.save}
                  loadingDelete={loading.delete}
                  disableSave={Object.keys(errors).length > 0}
                />
              </div>

              <div className="tw:p-4">
                <QuizBuilder
                  initialQuiz={{
                    ...quizObj,
                    id: quizId || quizObj.id,
                    videoId: videoId || quizObj.videoId,
                    title: quizTitle || quizObj.title,
                  }}
                  onChange={setQuizObj}
                  key={`quiz-${quizId}-${builderKey}`}
                  lockedQuestionIds={lockedQuestionIds}
                  hasResults={hasResults}
                />
              </div>
              <div className="tw:flex tw:justify-end tw:px-6 tw:py-4 tw:bg-gray-50">
                <QuizActionBar
                  onLoad={loadQuiz}
                  onSave={saveQuiz}
                  onDelete={deleteQuiz}
                  loadingLoad={loading.load}
                  loadingSave={loading.save}
                  loadingDelete={loading.delete}
                  disableSave={Object.keys(errors).length > 0}
                />
              </div>
            </section>
          )}

          {!selected && (
            <div className="tw:text-center tw:py-12 tw:text-gray-500">
              <div className="tw:text-5xl">🎬</div>
              <p className="tw:mt-3">Seleziona un video per iniziare a costruire il quiz.</p>
            </div>
          )}

          {/* <QuizResults token={token} isAllowed={isAllowed} videos={videos} /> */}
        </>
      )}

      {/* === Modal di conferma === */}
      {confirmModal.open && (
        <div
          className="tw:fixed tw:inset-0 tw:bg-black/50 tw:z-50 tw:flex tw:items-center tw:justify-center tw:p-4"
          onClick={() => setConfirmModal({ open: false, onConfirm: null, message: "" })}
        >
          <div
            className="tw:bg-white tw:rounded-xl tw:shadow-xl tw:w-full tw:max-w-sm tw:p-6 tw:space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tw:text-center">
              <div className="tw:w-12 tw:h-12 tw:mx-auto tw:bg-amber-100 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-amber-600">
                <FontAwesomeIcon icon={faExclamationTriangle} className="tw:text-xl" />
              </div>
              <h3 className="tw:mt-4 tw:text-lg tw:font-semibold tw:text-gray-800">{confirmModal.message}</h3>
            </div>
            <div className="tw:flex tw:gap-3 tw:pt-2">
              <button
                type="button"
                className="tw:flex-1 tw:px-4 tw:py-2.5 tw:bg-gray-200 hover:tw:bg-gray-300 tw:text-gray-800 tw:font-medium tw:rounded-sm tw:transition"
                onClick={() => setConfirmModal({ open: false, onConfirm: null, message: "" })}
              >
                Annulla
              </button>
              <button
                type="button"
                className="tw:flex-1 tw:px-4 tw:py-2.5 tw:bg-rose-600 hover:tw:bg-rose-700 tw:text-white tw:font-medium tw:rounded-sm tw:transition"
                onClick={confirmModal.onConfirm}
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
